import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useCreateOrganizationMutation } from "@/hooks/queries/useOrganizationsQuery";
import type { CreateOrganizationCommand } from "@/types/organization";

const AddOrganization = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const createOrganizationMutation = useCreateOrganizationMutation();
  const [formData, setFormData] = useState({ name: "", description: "", orgNumber: "", contactEmail: "", country: "", owner: "" });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const command: CreateOrganizationCommand = {
      name: formData.name,
      description: formData.description || null,
      orgNumber: formData.orgNumber || null,
      contactEmail: formData.contactEmail || null,
      country: formData.country || null,
      owner: formData.owner || null,
    };
    const organization = await createOrganizationMutation.mutateAsync(command);
    navigate(`/organization/${organization.id}`);
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/organization")} aria-label={t("organizations")}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("createNewOrganization")}</h1>
            <p className="text-muted-foreground">{t("fillOrganizationDetails")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-card p-6 rounded-lg border border-border">
          <div className="space-y-2">
            <Label htmlFor="organization-name">{t("organizationName")} *</Label>
            <Input id="organization-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="organization-description">{t("description")}</Label>
            <Textarea id="organization-description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="organization-number">{t("organizationNumber")}</Label>
              <Input id="organization-number" value={formData.orgNumber} onChange={(e) => setFormData({ ...formData, orgNumber: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization-email">{t("contactEmail")}</Label>
              <Input id="organization-email" type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="organization-country">{t("country")}</Label>
              <Input id="organization-country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization-owner">{t("organizationOwner")}</Label>
              <Input id="organization-owner" value={formData.owner} onChange={(e) => setFormData({ ...formData, owner: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-4 pt-4">
            <Button type="submit" className="w-full sm:flex-1" disabled={createOrganizationMutation.isPending}>
              {createOrganizationMutation.isPending && <Loader2 className="animate-spin mr-2" size={16} />}
              {t("createOrganization")}
            </Button>
            <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => navigate("/organization")} disabled={createOrganizationMutation.isPending}>{t("cancel")}</Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default AddOrganization;
